import React, { useState, useEffect } from 'react';
import {
    Card, Typography, Table, Tag, Button, Select, DatePicker,
    message, Space, Tabs, Descriptions, Drawer, Row, Col,
    Statistic, Input, Grid
} from 'antd';
import {
    SearchOutlined, ArrowLeftOutlined, FileTextOutlined,
    DollarOutlined, TeamOutlined
} from '@ant-design/icons';
import client from '../api/client';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const fmt = (val) =>
    `₱${Number(val ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const balanceColor = (val) => (Number(val) > 0 ? '#dc2626' : '#059669');

// ────────────────────────────────────────────────────────
// AR — Accounts Receivable
// ────────────────────────────────────────────────────────
const ARTab = () => {
    const [summary, setSummary] = useState([]);
    const [totalAr, setTotalAr] = useState(0);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [asOf, setAsOf] = useState(dayjs());
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [selectedPartner, setSelectedPartner] = useState(null);
    const [ledger, setLedger] = useState(null);
    const [ledgerLoading, setLedgerLoading] = useState(false);
    const [dateRange, setDateRange] = useState(null);

    const fetch = async () => {
        setLoading(true);
        try {
            const { data } = await client.get('/ledgers/ar', {
                params: { as_of_date: asOf?.format('YYYY-MM-DD') },
            });
            setSummary(data.summary || []);
            setTotalAr(data.total_ar || 0);
        } catch {
            message.error('Failed to load AR ledger');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetch(); }, [asOf]);

    const openDetail = async (partner) => {
        setSelectedPartner(partner);
        setDrawerOpen(true);
        setLedgerLoading(true);
        try {
            const params = {};
            if (dateRange) {
                params.start_date = dateRange[0].format('YYYY-MM-DD');
                params.end_date   = dateRange[1].format('YYYY-MM-DD');
            }
            const { data } = await client.get(`/ledgers/ar/${partner.partner_id}`, { params });
            setLedger(data);
        } catch {
            message.error('Failed to load customer detail');
        } finally {
            setLedgerLoading(false);
        }
    };

    const filtered = summary.filter(r =>
        r.partner_name?.toLowerCase().includes(search.toLowerCase())
    );

    const summaryColumns = [
        { title: 'Customer', dataIndex: 'partner_name', render: (n) => <Text strong>{n}</Text> },
        { title: 'Invoices', dataIndex: 'invoice_count', align: 'center', width: 90 },
        { title: 'Total Billed', dataIndex: 'total_billed', align: 'right', render: fmt },
        { title: 'Total Paid', dataIndex: 'total_paid', align: 'right', render: (v) => <Text style={{ color: '#059669' }}>{fmt(v)}</Text> },
        {
            title: 'Balance', dataIndex: 'total_balance', align: 'right',
            render: (v) => <Text strong style={{ color: balanceColor(v) }}>{fmt(v)}</Text>,
            sorter: (a, b) => b.total_balance - a.total_balance,
            defaultSortOrder: 'ascend',
        },
        {
            title: '', align: 'right', width: 100,
            render: (_, r) => (
                <Button size="small" icon={<FileTextOutlined />} onClick={() => openDetail(r)}>
                    Ledger
                </Button>
            ),
        },
    ];

    const ledgerColumns = [
        { title: 'Date', dataIndex: 'date', width: 110, render: (d) => dayjs(d).format('MMM D, YYYY') },
        { title: 'Type', dataIndex: 'type', width: 90, render: (t) => <Tag color={t === 'Invoice' ? 'blue' : 'green'}>{t}</Tag> },
        { title: 'Reference', dataIndex: 'reference', width: 140 },
        { title: 'Description', dataIndex: 'description', ellipsis: true },
        { title: 'Debit', dataIndex: 'debit', align: 'right', width: 110, render: (v) => v > 0 ? <Text style={{ color: '#dc2626' }}>{fmt(v)}</Text> : '—' },
        { title: 'Credit', dataIndex: 'credit', align: 'right', width: 110, render: (v) => v > 0 ? <Text style={{ color: '#059669' }}>{fmt(v)}</Text> : '—' },
        { title: 'Balance', dataIndex: 'balance', align: 'right', width: 110, render: (v) => <Text strong style={{ color: balanceColor(v) }}>{fmt(v)}</Text> },
    ];

    return (
        <div style={{ marginTop: 24 }}>
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                <Col xs={24} sm={8}>
                    <Card className="premium-card">
                        <Statistic
                            title="Total Outstanding AR"
                            value={totalAr}
                            prefix="₱"
                            precision={2}
                            valueStyle={{ color: '#dc2626', fontWeight: 700 }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={8}>
                    <Card className="premium-card">
                        <Statistic
                            title="Customers with Balance"
                            value={summary.filter(r => r.total_balance > 0).length}
                            suffix="customers"
                        />
                    </Card>
                </Col>
            </Row>

            <Card className="premium-card" bodyStyle={{ padding: 0 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 24px', borderBottom: '1px solid rgba(148,163,184,0.15)' }}>
                    <Input
                        placeholder="Search customer..."
                        prefix={<SearchOutlined />}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        style={{ width: 240, borderRadius: 8 }}
                        allowClear
                    />
                    <Space>
                        <DatePicker
                            value={asOf}
                            onChange={setAsOf}
                            placeholder="As of date"
                            style={{ borderRadius: 8 }}
                        />
                        <Button type="primary" onClick={fetch} style={{ borderRadius: 8 }}>
                            Refresh
                        </Button>
                    </Space>
                </div>
                <Table
                    dataSource={filtered}
                    columns={summaryColumns}
                    loading={loading}
                    rowKey="partner_id"
                    pagination={{ defaultPageSize: 15 }}
                />
            </Card>

            <Drawer
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                title={selectedPartner ? `AR Ledger — ${selectedPartner.partner_name}` : 'AR Ledger'}
                width={900}
                extra={
                    <RangePicker
                        value={dateRange}
                        onChange={(v) => { setDateRange(v); if (selectedPartner) openDetail(selectedPartner); }}
                        style={{ borderRadius: 8 }}
                    />
                }
            >
                {ledger && (
                    <>
                        <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
                            <Col span={8}><Statistic title="Total Billed" value={ledger.total_billed} prefix="₱" precision={2} /></Col>
                            <Col span={8}><Statistic title="Total Paid" value={ledger.total_paid} prefix="₱" precision={2} valueStyle={{ color: '#059669' }} /></Col>
                            <Col span={8}><Statistic title="Outstanding" value={ledger.outstanding_balance} prefix="₱" precision={2} valueStyle={{ color: '#dc2626', fontWeight: 700 }} /></Col>
                        </Row>
                        <Table
                            dataSource={ledger.ledger}
                            columns={ledgerColumns}
                            loading={ledgerLoading}
                            rowKey={(_, i) => i}
                            pagination={{ defaultPageSize: 20 }}
                            size="small"
                            scroll={{ x: 700 }}
                        />
                    </>
                )}
            </Drawer>
        </div>
    );
};

// ────────────────────────────────────────────────────────
// AP — Accounts Payable
// ────────────────────────────────────────────────────────
const APTab = () => {
    const [summary, setSummary] = useState([]);
    const [totalAp, setTotalAp] = useState(0);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [asOf, setAsOf] = useState(dayjs());
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [selectedPartner, setSelectedPartner] = useState(null);
    const [ledger, setLedger] = useState(null);
    const [ledgerLoading, setLedgerLoading] = useState(false);
    const [dateRange, setDateRange] = useState(null);

    const fetch = async () => {
        setLoading(true);
        try {
            const { data } = await client.get('/ledgers/ap', {
                params: { as_of_date: asOf?.format('YYYY-MM-DD') },
            });
            setSummary(data.summary || []);
            setTotalAp(data.total_ap || 0);
        } catch {
            message.error('Failed to load AP ledger');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetch(); }, [asOf]);

    const openDetail = async (partner) => {
        setSelectedPartner(partner);
        setDrawerOpen(true);
        setLedgerLoading(true);
        try {
            const params = {};
            if (dateRange) {
                params.start_date = dateRange[0].format('YYYY-MM-DD');
                params.end_date   = dateRange[1].format('YYYY-MM-DD');
            }
            const { data } = await client.get(`/ledgers/ap/${partner.partner_id}`, { params });
            setLedger(data);
        } catch {
            message.error('Failed to load supplier detail');
        } finally {
            setLedgerLoading(false);
        }
    };

    const filtered = summary.filter(r =>
        r.partner_name?.toLowerCase().includes(search.toLowerCase())
    );

    const summaryColumns = [
        { title: 'Supplier', dataIndex: 'partner_name', render: (n) => <Text strong>{n}</Text> },
        { title: 'Purchases', dataIndex: 'purchase_count', align: 'center', width: 100 },
        { title: 'Total Purchased', dataIndex: 'total_billed', align: 'right', render: fmt },
        { title: 'Total Paid', dataIndex: 'total_paid', align: 'right', render: (v) => <Text style={{ color: '#059669' }}>{fmt(v)}</Text> },
        {
            title: 'Balance Owed', dataIndex: 'total_balance', align: 'right',
            render: (v) => <Text strong style={{ color: balanceColor(v) }}>{fmt(v)}</Text>,
            sorter: (a, b) => b.total_balance - a.total_balance,
            defaultSortOrder: 'ascend',
        },
        {
            title: '', align: 'right', width: 100,
            render: (_, r) => (
                <Button size="small" icon={<FileTextOutlined />} onClick={() => openDetail(r)}>
                    Ledger
                </Button>
            ),
        },
    ];

    const ledgerColumns = [
        { title: 'Date', dataIndex: 'date', width: 110, render: (d) => dayjs(d).format('MMM D, YYYY') },
        { title: 'Type', dataIndex: 'type', width: 90, render: (t) => <Tag color={t === 'Purchase' ? 'orange' : 'green'}>{t}</Tag> },
        { title: 'Reference', dataIndex: 'reference', width: 140 },
        { title: 'Description', dataIndex: 'description', ellipsis: true },
        { title: 'Debit', dataIndex: 'debit', align: 'right', width: 110, render: (v) => v > 0 ? <Text style={{ color: '#059669' }}>{fmt(v)}</Text> : '—' },
        { title: 'Credit', dataIndex: 'credit', align: 'right', width: 110, render: (v) => v > 0 ? <Text style={{ color: '#dc2626' }}>{fmt(v)}</Text> : '—' },
        { title: 'Balance', dataIndex: 'balance', align: 'right', width: 110, render: (v) => <Text strong style={{ color: balanceColor(v) }}>{fmt(v)}</Text> },
    ];

    return (
        <div style={{ marginTop: 24 }}>
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                <Col xs={24} sm={8}>
                    <Card className="premium-card">
                        <Statistic
                            title="Total Outstanding AP"
                            value={totalAp}
                            prefix="₱"
                            precision={2}
                            valueStyle={{ color: '#dc2626', fontWeight: 700 }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={8}>
                    <Card className="premium-card">
                        <Statistic
                            title="Suppliers with Balance"
                            value={summary.filter(r => r.total_balance > 0).length}
                            suffix="suppliers"
                        />
                    </Card>
                </Col>
            </Row>

            <Card className="premium-card" bodyStyle={{ padding: 0 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 24px', borderBottom: '1px solid rgba(148,163,184,0.15)' }}>
                    <Input
                        placeholder="Search supplier..."
                        prefix={<SearchOutlined />}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        style={{ width: 240, borderRadius: 8 }}
                        allowClear
                    />
                    <Space>
                        <DatePicker
                            value={asOf}
                            onChange={setAsOf}
                            placeholder="As of date"
                            style={{ borderRadius: 8 }}
                        />
                        <Button type="primary" onClick={fetch} style={{ borderRadius: 8 }}>
                            Refresh
                        </Button>
                    </Space>
                </div>
                <Table
                    dataSource={filtered}
                    columns={summaryColumns}
                    loading={loading}
                    rowKey="partner_id"
                    pagination={{ defaultPageSize: 15 }}
                />
            </Card>

            <Drawer
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                title={selectedPartner ? `AP Ledger — ${selectedPartner.partner_name}` : 'AP Ledger'}
                width={900}
                extra={
                    <RangePicker
                        value={dateRange}
                        onChange={(v) => { setDateRange(v); if (selectedPartner) openDetail(selectedPartner); }}
                        style={{ borderRadius: 8 }}
                    />
                }
            >
                {ledger && (
                    <>
                        <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
                            <Col span={8}><Statistic title="Total Purchased" value={ledger.total_purchased} prefix="₱" precision={2} /></Col>
                            <Col span={8}><Statistic title="Total Paid" value={ledger.total_paid} prefix="₱" precision={2} valueStyle={{ color: '#059669' }} /></Col>
                            <Col span={8}><Statistic title="Outstanding" value={ledger.outstanding_balance} prefix="₱" precision={2} valueStyle={{ color: '#dc2626', fontWeight: 700 }} /></Col>
                        </Row>
                        <Table
                            dataSource={ledger.ledger}
                            columns={ledgerColumns}
                            loading={ledgerLoading}
                            rowKey={(_, i) => i}
                            pagination={{ defaultPageSize: 20 }}
                            size="small"
                            scroll={{ x: 700 }}
                        />
                    </>
                )}
            </Drawer>
        </div>
    );
};

// ────────────────────────────────────────────────────────
// Main Page
// ────────────────────────────────────────────────────────
const SubsidiaryLedgerPage = () => {
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.md;

    return (
        <div style={{ padding: isMobile ? '16px' : '24px 32px' }}>
            <div style={{ marginBottom: 32 }}>
                <Title level={2} style={{ margin: 0, fontWeight: 800 }}>Subsidiary Ledgers</Title>
                <Text style={{ color: 'var(--text-secondary)', fontSize: 16 }}>
                    Track outstanding receivables and payables by partner.
                </Text>
            </div>

            <Card className="premium-card" bodyStyle={{ padding: isMobile ? '0 12px 16px 12px' : '0 24px 24px 24px' }}>
                <Tabs
                    defaultActiveKey="ar"
                    size="large"
                    items={[
                        {
                            key: 'ar',
                            label: <span><DollarOutlined style={{ marginRight: 6 }} />Accounts Receivable</span>,
                            children: <ARTab />,
                        },
                        {
                            key: 'ap',
                            label: <span><TeamOutlined style={{ marginRight: 6 }} />Accounts Payable</span>,
                            children: <APTab />,
                        },
                    ]}
                />
            </Card>
        </div>
    );
};

export default SubsidiaryLedgerPage;
